import { getThemeById, DEFAULT_THEME_ID } from '@/constants/themes';

/**
 * Marketing design tokens — kept in a plain (non-"use client") module so that
 * BOTH Server Components (the legal pages, pricing, module pages) and Client
 * Components (MarketingShell) import the real string values.
 *
 * This split matters: when a Server Component imports from a "use client"
 * module, React hands it a client-reference proxy, so reading token values
 * (e.g. `MARKETING_TOKENS.BORDER`) yields `undefined` on the server and the
 * resulting `sx` borders/colours silently drop out. Living here, every
 * importer gets the concrete value regardless of the RSC boundary.
 *
 * Marketing palette is derived from the committed product theme (see
 * src/constants/themes.ts) so the public site matches the app. Swapping
 * DEFAULT_THEME_ID re-skins marketing too. Every token resolves to a `--mk-*`
 * CSS variable, falling back to the committed default theme when the var is
 * unset, so any ancestor that sets `--mk-*` via marketingVars() can re-skin the
 * entire marketing surface live. See src/lib/marketingTheme.ts.
 */
const _t = getThemeById(DEFAULT_THEME_ID);

const _rgb = (hex: string) => {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};
const _primRgb = _rgb(_t.primary);
const _textRgb = _rgb(_t.text);
const _bgRgb = _rgb(_t.bg);

// Translucent page background for the sticky header / blur bars.
export const MARKETING_BG_TRANSLUCENT = `rgba(var(--mk-bg-rgb, ${_bgRgb}), 0.82)`;

/**
 * The one public address, in one place.
 *
 * It had drifted: the API, its email templates, webpush, the marketing footer
 * and the cookie policy all used support@, while the contact page had invented
 * hello@ — so the contact page showed BOTH, its own in the support card and the
 * real one in the footer directly below. Mirrors SUPPORT_EMAIL in
 * Builder-API/services/email/templates.js; if that ever moves, this moves with
 * it.
 */
export const SUPPORT_EMAIL = 'support@buildersitenow.com';

export const MARKETING_TOKENS = {
  BG:            `var(--mk-bg, ${_t.bg})`,
  SURFACE:       `var(--mk-surface, ${_t.surface})`,
  SURFACE_ALT:   `var(--mk-surface-alt, ${_t.surfaceAlt})`,
  BORDER:        `var(--mk-border, ${_t.border})`,
  BORDER_SOFT:   `rgba(var(--mk-text-rgb, ${_textRgb}), 0.08)`,
  TEXT:          `var(--mk-text, ${_t.text})`,
  TEXT_MUTED:    `var(--mk-muted, ${_t.muted})`,
  TEXT_SOFT:     `rgba(var(--mk-text-rgb, ${_textRgb}), 0.70)`,
  // PRIMARY is the brand surface colour — fine for button fills, gradients and
  // ≥18pt headings. For small body/eyebrow text on tinted surfaces use PRIMARY_DARK.
  PRIMARY:       `var(--mk-primary, ${_t.primary})`,
  PRIMARY_DARK:  `var(--mk-primary-dark, ${_t.primaryDark})`,
  ON_PRIMARY:    `var(--mk-on-primary, ${_t.onPrimary})`,
  ACCENT:        `var(--mk-accent, ${_t.accent})`,
  // Negative / "what's broken today" accent (problem bullets, ✗ marks). Has a
  // sensible brick-red default; a marketing theme can override via --mk-danger.
  DANGER:        `var(--mk-danger, #C0392B)`,
  DANGER_SOFT:   `rgba(var(--mk-danger-rgb, 192,57,43), 0.10)`,
  // Legacy aliases (kept so older consumers that import these names resolve).
  ACCENT_GLOW:   `rgba(var(--mk-primary-rgb, ${_primRgb}), 0.22)`,
  TEXT_LIGHT:    `var(--mk-text, ${_t.text})`,
  TEXT_DIM:      `var(--mk-muted, ${_t.muted})`,
  SURFACE_LINE:  `var(--mk-border, ${_t.border})`,
  SURFACE_LINE_STRONG: `rgba(var(--mk-text-rgb, ${_textRgb}), 0.12)`,
  ACCENT_LIGHT:  `var(--mk-primary-light, #10B981)`,
};
